// Where to send someone who needs to sign in, and where to send them back afterwards.
//
// The way back rides in the login URL (`/?redirect=<path>`) rather than in router state or
// localStorage: it survives a reload of the login page, it works from fetchWrapper's 401
// handler, which leaves the app with `window.location` and has no router to hand state to,
// and it belongs to that one tab instead of every tab sharing a storage key.

export const REDIRECT_PARAM = "redirect";
export const DEFAULT_AFTER_LOGIN = "/business/backoffice";

// The login screen itself is "/", so there is nothing to come back to from there.
export function loginPathFor(location) {
  const pathname = location?.pathname || "/";
  if (pathname === "/") return "/";
  const target = `${pathname}${location.search || ""}${location.hash || ""}`;
  return `/?${REDIRECT_PARAM}=${encodeURIComponent(target)}`;
}

// Control characters and whitespace never appear in a path loginPathFor wrote (location keeps
// them percent-encoded), and the URL parser silently drops tabs and newlines — which is how
// "/\t/evil.com" would pass a prefix check and still be read as "//evil.com".
const UNSAFE_CHARACTERS = /[\u0000- \u007F]/; // eslint-disable-line no-control-regex

// The value comes from the URL, so anyone can write it: only a path on this same site is
// followed. The decision is the browser's own URL parser's rather than a set of string
// prefixes, so "//evil.com", "/\evil.com" and whatever other spelling a browser resolves to
// another host are all judged by the rule the navigation itself would use. A value with a
// scheme ("https:", "javascript:") is refused before that, for not being a path at all.
// What is returned is the parsed path, never the raw input.
export function safeRedirectPath(raw, origin = window.location.origin) {
  if (typeof raw !== "string" || !raw.startsWith("/")) return null;
  if (UNSAFE_CHARACTERS.test(raw)) return null;

  let url;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }

  if (url.origin !== new URL(origin).origin) return null;
  // The login itself: nothing to come back to, and following it would land a signed-in
  // person on the login screen.
  if (url.pathname === "/") return null;
  return `${url.pathname}${url.search}${url.hash}`;
}
